// The sheet's single table: four spanning groups over eleven columns, one row
// per cargo, the batch-wide services a legacy document still carries, and the
// row that adds the whole calculation up.
import { Fragment, useState, type FocusEvent } from 'react';
import {
  LOGISTICS_CALCULATION_SHEET_CHROME,
  type LogisticsCalculationPublicDto,
  pluralRu,
} from '@magimp/shared';
import { DocumentCargoRow } from './DocumentCargoRow.js';
import { useDocumentEditing } from './DocumentCell.js';
import { Amounts } from './DocumentCost.js';
import { LegacyLines } from './DocumentExtras.js';
import {
  DOCUMENT_VOLUME_UNIT,
  DOCUMENT_WEIGHT_UNIT,
  formatDecimal,
  nonZeroAmounts,
  settlementLine,
  sumMeasures,
} from './document-format.js';
import {
  SHEET_COST_FIELDS,
  cargoRowAmounts,
  columnTotals,
  commonServicesTarget,
  renderedLegacyLines,
  type CargoRowAmounts,
} from './sheet-amounts.js';

const doc = LOGISTICS_CALCULATION_SHEET_CHROME.document;
const GROUPS = doc.tableGroupHeaders;
const COLUMNS = doc.tableColumnHeaders;
const NUMBER_SIGN = '№';
/** Column indices (into `COLUMNS`) that open a section after the first. */
const SECTION_STARTS = new Set([5, 9]);
/** The header group a cell index of a row belongs to; the numbering column has none. */
function groupOf(cellIndex: number): number {
  if (cellIndex >= 12) return 3;
  if (cellIndex >= 10) return 2;
  if (cellIndex >= 6) return 1;
  return cellIndex >= 1 ? 0 : -1;
}

// WHY from the chrome rather than i18n: the sheet must read Russian whatever the
// operator's UI language is, so its count nouns come from the frozen customer
// copy and are declined here.

/** Reference alignment: identity left, counts centred, measures and money right. */
const COLUMN_CLASSES = [
  'doc-idw doc-pin doc-pin-end',
  'doc-road',
  'doc-c',
  'doc-r',
  'doc-c',
  'doc-r',
  'doc-r',
  'doc-r',
  'doc-r',
  'doc-r',
  'doc-r',
];

export function DocumentTable({
  dto,
  cargoCount,
  cargoKeys,
  onRemoveCargo,
}: {
  dto: LogisticsCalculationPublicDto | null;
  cargoCount: number;
  cargoKeys?: readonly string[] | undefined;
  onRemoveCargo?: ((index: number) => void) | undefined;
}) {
  const rows = Array.from({ length: cargoCount }, (_unused, index) =>
    cargoRowAmounts(dto?.cargos[index] ?? null),
  );
  const commonLines = renderedLegacyLines(
    dto?.commonServices ?? [],
    commonServicesTarget(rows, dto?.totals ?? null),
  );
  const [activeColumn, track] = useActiveColumn();
  return (
    <div className="doc-scroll">
      <table className="doc-grid">
        <TableHead activeColumn={activeColumn} />
        <tbody {...track}>
          {rows.map((amounts, index) => (
            <DocumentCargoRow
              key={cargoKeys?.[index] ?? `${dto?.cargos[index]?.article ?? ''}-${index}`}
              index={index}
              cargo={dto?.cargos[index] ?? null}
              amounts={amounts}
              rates={dto}
              onRemove={onRemoveCargo ? () => onRemoveCargo(index) : undefined}
            />
          ))}
          {commonLines.length > 0 ? <CommonServicesRow lines={commonLines} /> : null}
          <TotalsRow dto={dto} rows={rows} commonLines={commonLines} />
        </tbody>
      </table>
    </div>
  );
}

/**
 * Which column of the table holds focus, while editing. The header names it so
 * the manager typing in the tenth row need not look up to find out where he is.
 * Cleared when focus leaves the table body, kept when it moves between cells.
 */
function useActiveColumn(): [
  number | null,
  {
    onFocusCapture?: (event: FocusEvent<HTMLTableSectionElement>) => void;
    onBlurCapture?: (event: FocusEvent<HTMLTableSectionElement>) => void;
  },
] {
  const editing = useDocumentEditing();
  const [active, setActive] = useState<number | null>(null);
  if (!editing) return [null, {}];
  return [
    active,
    {
      onFocusCapture: (event) => {
        const cell = (event.target as HTMLElement).closest('td');
        setActive(cell ? cell.cellIndex : null);
      },
      onBlurCapture: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActive(null);
      },
    },
  ];
}

function TableHead({ activeColumn }: { activeColumn: number | null }) {
  const editing = useDocumentEditing();
  const group = activeColumn === null ? -1 : groupOf(activeColumn);
  const on = (match: boolean) => (match ? '' : undefined);
  return (
    <thead>
      <tr>
        <th rowSpan={2} className="doc-c doc-w-no doc-pin">
          {NUMBER_SIGN}
        </th>
        {/* WHY: while editing the article column is pinned, and a group cell
            spanning it would scroll its label through the pinned band. A blank
            pinned cell covers that column; the label spans the four measures. */}
        {editing ? (
          <th className="doc-grp doc-idw doc-pin doc-pin-end" data-active={on(group === 0)} />
        ) : null}
        <th colSpan={editing ? 4 : 5} className="doc-grp" data-active={on(group === 0)}>
          {GROUPS[0]}
        </th>
        <th colSpan={4} className="doc-grp" data-section-start="" data-active={on(group === 1)}>
          {GROUPS[1]}
        </th>
        <th colSpan={2} className="doc-grp" data-section-start="" data-active={on(group === 2)}>
          {GROUPS[2]}
        </th>
        <th
          rowSpan={2}
          className="doc-r doc-w-tot"
          data-section-start=""
          data-active={on(group === 3)}
        >
          {GROUPS[3]}
        </th>
      </tr>
      <tr>
        {COLUMNS.map((column, index) => (
          <th
            key={column}
            className={COLUMN_CLASSES[index]}
            data-section-start={on(SECTION_STARTS.has(index))}
            data-active={on(activeColumn === index + 1)}
          >
            <ColumnLabel label={column} />
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** A heading the reference breaks over two lines stays broken, not re-spaced. */
function ColumnLabel({ label }: { label: string }) {
  return (
    <>
      {label.split('\n').map((line, index) => (
        <Fragment key={line}>
          {index === 0 ? null : <br />}
          {line}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Services a legacy document booked against the whole batch rather than one
 * cargo. They print in the extras column so the column above them adds up.
 */
function CommonServicesRow({ lines }: { lines: ReturnType<typeof renderedLegacyLines> }) {
  return (
    <tr data-testid="rl-document-common-services">
      <td className="doc-c doc-muted" />
      <td colSpan={8}>
        {doc.commonServicesTitle}
        <span className="doc-sm">{doc.commonServicesHint}</span>
      </td>
      <td className="doc-r doc-n">
        <LegacyLines path="commonServices" lines={lines} />
      </td>
      <td colSpan={3} />
    </tr>
  );
}

function TotalsRow({
  dto,
  rows,
  commonLines,
}: {
  dto: LogisticsCalculationPublicDto | null;
  rows: readonly CargoRowAmounts[];
  commonLines: ReturnType<typeof renderedLegacyLines>;
}) {
  // The batch-wide services are a row of the table, so the column sums count
  // them exactly as they count a cargo's own stored lines.
  const columns =
    commonLines.length === 0 ? rows : [...rows, { ...cargoRowAmounts(null), legacy: commonLines }];
  const settlement = settlementLine(dto?.totals, dto);
  return (
    <tr className="doc-sum" data-testid="rl-document-totals-row">
      {/* Two cells, not one: the caption pins with the identity columns while
          editing, and a cell spanning the measures would cover them. */}
      <td colSpan={2} className="doc-pin doc-pin-end">
        {`${doc.grandRowCaption} · ${rows.length} ${pluralRu(rows.length, doc.cargoCountForms)}`}
      </td>
      <td colSpan={4}>{totalsMeasures(dto)}</td>
      {SHEET_COST_FIELDS.map((field) => (
        <td
          className="doc-r doc-n"
          key={field}
          data-section-start={field === 'transport' || field === 'prr' ? '' : undefined}
        >
          <Amounts amounts={nonZeroAmounts(columnTotals(columns, field))} />
        </td>
      ))}
      <td className="doc-r doc-n doc-tot" data-total="" data-section-start="">
        <Amounts amounts={nonZeroAmounts(dto?.totals)} bold />
        {settlement ? <span className="doc-eq">{settlement}</span> : null}
      </td>
    </tr>
  );
}

/**
 * INVARIANT: a measure only one cargo states as prose is left out of the line
 * rather than summed over the rest — a partial sum would read as a total.
 */
function totalsMeasures(dto: LogisticsCalculationPublicDto | null): string {
  const cargos = dto?.cargos ?? [];
  const seats = sumMeasures(cargos.map((cargo) => cargo.seats));
  const weight = sumMeasures(cargos.map((cargo) => cargo.weight));
  const volume = sumMeasures(cargos.map((cargo) => cargo.volume));
  const parts: string[] = [];
  if (seats !== null) {
    parts.push(`${formatDecimal(seats, 0)} ${pluralRu(Math.round(seats), doc.seatCountForms)}`);
  }
  if (weight !== null) parts.push(`${formatDecimal(weight, 3)} ${DOCUMENT_WEIGHT_UNIT}`);
  if (volume !== null) parts.push(`${formatDecimal(volume, 3)} ${DOCUMENT_VOLUME_UNIT}`);
  return parts.join(' · ');
}
